<template>
  <div v-if="visible" class="pdf-full-overlay">
    <header class="pdf-toolbar">
      <div class="pdf-toolbar-left">
        <button
          class="pdf-icon-btn"
          type="button"
          title="Chiudi"
          @click="close"
        >
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <div class="pdf-title">
          <i class="fa-regular fa-file-pdf"></i>
          <span>{{ title }}</span>
        </div>
      </div>

      <div class="pdf-toolbar-center">
        <button
          class="pdf-icon-btn"
          type="button"
          title="Pagina precedente"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="pdf-page-current">{{ currentPage }}</span>
        <span class="pdf-page-total">/ {{ totalPages || "-" }}</span>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Pagina successiva"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >
          <i class="fa-solid fa-chevron-right"></i>
        </button>
        <span class="pdf-toolbar-divider"></span>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Riduci zoom"
          @click="zoomOut"
        >
          <i class="fa-solid fa-minus"></i>
        </button>
        <span class="pdf-zoom-value">{{ Math.round(zoom * 100) }}%</span>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Aumenta zoom"
          @click="zoomIn"
        >
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>

      <div class="pdf-toolbar-right">
        <button
          class="pdf-icon-btn"
          type="button"
          title="Ruota di 90 gradi"
          @click="rotateClockwise"
        >
          <i class="fa-solid fa-rotate-right"></i>
        </button>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Apri in nuova scheda"
          @click="openInNewTab"
        >
          <i class="fa-solid fa-up-right-from-square"></i>
        </button>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Scarica"
          @click="$emit('download')"
        >
          <i class="fa-solid fa-download"></i>
        </button>
        <button
          class="pdf-icon-btn"
          type="button"
          title="Stampa"
          @click="printPdf"
        >
          <i class="fa-solid fa-print"></i>
        </button>
      </div>
    </header>

    <main
      ref="scrollArea"
      class="pdf-scroll-area"
      @click="handleOverlayClick"
      @scroll="handleScroll"
    >
      <div v-if="loading" class="pdf-loading">
        <span class="pdf-loading-spinner"></span>
        Caricamento PDF...
      </div>
      <div v-else-if="error" class="pdf-error">
        {{ error }}
      </div>
      <div v-else class="pdf-pages">
        <section
          v-for="page in pages"
          :key="page.number"
          :ref="`pageWrap${page.number}`"
          class="pdf-page-wrap"
        >
          <canvas :ref="`pageCanvas${page.number}`"></canvas>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: "PDFViewerFull",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    src: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Documento PDF",
    },
  },
  data() {
    return {
      pdfDocument: null,
      pages: [],
      totalPages: 0,
      currentPage: 1,
      zoom: 1,
      rotation: 0,
      loading: false,
      error: "",
      renderVersion: 0,
      renderTasks: {},
      scrollTicking: false,
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        document.addEventListener("keydown", this.handleKeydown);
        this.loadPdf();
      } else {
        document.removeEventListener("keydown", this.handleKeydown);
      }
    },
    src() {
      if (this.visible) {
        this.loadPdf();
      }
    },
  },
  mounted() {
    if (this.visible) {
      document.addEventListener("keydown", this.handleKeydown);
      this.loadPdf();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.cancelRenderTasks();
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async loadPdf() {
      this.loading = true;
      this.error = "";
      this.currentPage = 1;
      this.rotation = 0;
      this.pages = [];
      this.totalPages = 0;
      this.cancelRenderTasks();
      if (this.pdfDocument) {
        await this.pdfDocument.destroy();
        this.pdfDocument = null;
      }

      try {
        const loadingTask = pdfjsLib.getDocument(this.src);
        this.pdfDocument = await loadingTask.promise;
        this.totalPages = this.pdfDocument.numPages;
        this.pages = Array.from({ length: this.totalPages }, (_, index) => ({
          number: index + 1,
        }));
        this.loading = false;
        await this.$nextTick();
        this.renderAllPages();
      } catch (error) {
        console.error("Errore rendering PDF:", error);
        this.error = "Impossibile visualizzare il PDF.";
        this.loading = false;
      }
    },
    async renderAllPages() {
      const version = ++this.renderVersion;
      for (const page of this.pages) {
        if (version !== this.renderVersion) return;
        await this.renderPage(page.number, version);
      }
    },
    async renderPage(pageNumber, version) {
      if (!this.pdfDocument) return;
      const canvasRef = this.$refs[`pageCanvas${pageNumber}`];
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
      if (!canvas) return;

      if (this.renderTasks[pageNumber]) {
        this.renderTasks[pageNumber].cancel();
      }

      try {
        const page = await this.pdfDocument.getPage(pageNumber);
        if (version !== this.renderVersion) return;
        const viewport = page.getViewport({
          scale: this.zoom,
          rotation: this.rotation,
        });
        const pixelRatio = window.devicePixelRatio || 1;
        const context = canvas.getContext("2d");

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, viewport.width, viewport.height);

        const renderTask = page.render({ canvasContext: context, viewport });
        this.renderTasks[pageNumber] = renderTask;
        await renderTask.promise;
        delete this.renderTasks[pageNumber];
      } catch (error) {
        if (error?.name !== "RenderingCancelledException") {
          console.error(`Errore rendering pagina ${pageNumber}:`, error);
        }
      }
    },
    cancelRenderTasks() {
      Object.values(this.renderTasks).forEach((task) => {
        try {
          task.cancel();
        } catch (error) {
          // Rendering cancellation can fail when a task already completed.
        }
      });
      this.renderTasks = {};
      this.renderVersion += 1;
    },
    zoomIn() {
      this.zoom = Math.min(this.zoom + 0.1, 2.4);
      this.rerenderAtCurrentScroll();
    },
    zoomOut() {
      this.zoom = Math.max(this.zoom - 0.1, 0.5);
      this.rerenderAtCurrentScroll();
    },
    rotateClockwise() {
      this.rotation = (this.rotation + 90) % 360;
      this.rerenderAtCurrentScroll();
    },
    async rerenderAtCurrentScroll() {
      this.cancelRenderTasks();
      await this.$nextTick();
      this.renderAllPages();
    },
    goToPage(pageNumber) {
      const nextPage = Math.min(Math.max(pageNumber, 1), this.totalPages || 1);
      const wrapRef = this.$refs[`pageWrap${nextPage}`];
      const pageWrap = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
      if (pageWrap) {
        pageWrap.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      this.currentPage = nextPage;
    },
    handleScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateCurrentPageFromScroll();
        this.scrollTicking = false;
      });
    },
    handleOverlayClick(event) {
      const clickedPage = event.target.closest(".pdf-page-wrap");
      if (clickedPage) return;
      this.close();
    },
    updateCurrentPageFromScroll() {
      const scrollArea = this.$refs.scrollArea;
      if (!scrollArea) return;
      const top = scrollArea.getBoundingClientRect().top;
      let closestPage = this.currentPage;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.pages.forEach((page) => {
        const wrapRef = this.$refs[`pageWrap${page.number}`];
        const pageWrap = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
        if (!pageWrap) return;
        const distance = Math.abs(pageWrap.getBoundingClientRect().top - top);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = page.number;
        }
      });

      this.currentPage = closestPage;
    },
    openInNewTab() {
      window.open(this.src, "_blank", "noopener,noreferrer");
    },
    printPdf() {
      const printWindow = window.open(
        this.src,
        "_blank",
        "noopener,noreferrer"
      );
      if (printWindow) {
        window.setTimeout(() => {
          printWindow.print();
        }, 600);
      }
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.close();
      } else if (event.key === "ArrowLeft") {
        this.goToPage(this.currentPage - 1);
      } else if (event.key === "ArrowRight") {
        this.goToPage(this.currentPage + 1);
      }
    },
  },
};
</script>

<style scoped>
.pdf-full-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.62);
}

.pdf-toolbar {
  height: 44px;
  flex: 0 0 44px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  padding: 0 14px;
  background: #000000;
  color: #ffffff;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.08);
}

.pdf-toolbar-left,
.pdf-toolbar-center,
.pdf-toolbar-right {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pdf-toolbar-left {
  justify-content: flex-start;
}

.pdf-toolbar-center {
  justify-content: center;
}

.pdf-toolbar-right {
  justify-content: flex-end;
}

.pdf-title {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
}

.pdf-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pdf-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 3px;
  color: #ffffff;
  background: transparent;
  cursor: pointer;
}

.pdf-icon-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.pdf-icon-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.pdf-page-current,
.pdf-zoom-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 31px;
  height: 28px;
  padding: 0 7px;
  background: #0b0b0b;
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
}

.pdf-page-total {
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
}

.pdf-toolbar-divider {
  width: 1px;
  height: 26px;
  margin: 0 5px;
  background: rgba(255, 255, 255, 0.34);
}

.pdf-scroll-area {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: rgba(0, 0, 0, 0.38);
}

.pdf-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 12px 0 56px;
}

.pdf-page-wrap {
  background: #ffffff;
}

.pdf-page-wrap canvas {
  display: block;
}

.pdf-loading,
.pdf-error {
  min-height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  font-weight: 800;
}

.pdf-loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.28);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: pdf-spin 0.8s linear infinite;
}

@keyframes pdf-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .pdf-toolbar {
    height: auto;
    min-height: 44px;
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 6px 10px;
  }

  .pdf-toolbar-left,
  .pdf-toolbar-center,
  .pdf-toolbar-right {
    justify-content: center;
  }

  .pdf-toolbar-left {
    justify-content: flex-start;
  }
}
</style>
